import React, {Component} from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import {connect} from 'react-redux';
import {compose} from 'recompose';
import {
    addFromColumn,
    parseCurrency,
    toCurrency,
    sortArrayByKey,
    sortCurrency
} from '../shared/functions';
import {getGetHeaders} from '../shared/serverFunctions';
import {setDefaultTableSort, getTableState, filterCaseInsensitive} from '../shared/tableFunctions';
import {checkTokenStatus} from '../shared/tokenFunctions';
import ReactTable from 'react-table';
import {CSVLink} from 'react-csv';
import {RESOURCES as reportsResources} from '../fuseReportsResourceMap';
import ExpandableTableContainer from './ExpandableTableContainer';
import '../shared/react-table.css';
import {commonStyles} from '../shared/styles';
import {tableStyles} from '../shared/styles-table';
import CircularProgress from 'material-ui/CircularProgress';
import FlatButton from 'material-ui/FlatButton';
import ExportIcon from 'material-ui/svg-icons/navigation/arrow-downward';
import SalesSummaryIcon from 'material-ui/svg-icons/editor/insert-chart';
import Config from '../messages';

/**
 * Settlement Reports class for retrieving and displaying Sales Summary
 *
 */
class SettlementReportSales extends Component {
    constructor(props) {
        super(props);
        const reportName = (props.reportType === 'pre-settlement') ? 'Pre-Settlement' : 'Settlement';
        this.state = {
            displayTotals: true,
            errorMessage: null,
            priceList: [],
            reportName: reportName + ' Report Sales Summary',
            salesSummaryList: [],
            selectedTimeZoneOffset: null,
            selectedZeroDay: null,
            summaryComplete: false,
            tableData: [],
            tableHeaders: [
                {label: 'Date', key: 'date'},
                {label: 'Gross Cash Sales', key: 'cashGross'},
                {label: 'Net Cash Sales', key: 'cashNet'},
                {label: 'Gross Credit Card Sales', key: 'ccGross'},
                {label: 'Net Credit Card Sales', key: 'ccNet'},
                {label: 'Total Gross Sales', key: 'salesGross'},
                {label: 'Total Net Sales', key: 'salesNet'},
                {label: 'Tax', key: 'tax'},
            ],
        };
        this.handleExportClick = this.handleExportClick.bind(this);
        this.handleFilterChange = this.handleFilterChange.bind(this);
        this.handleSortChange = this.handleSortChange.bind(this);
    }

    componentWillMount() {
        this.generateSalesSummary(null);
        this.fetchData();
    }

    componentWillReceiveProps(nextProps) {
        let clearTable = false;
        if (
            nextProps.location.id !== this.props.location.id ||
            nextProps.timeZoneOffset !== this.props.timeZoneOffset ||
            nextProps.zeroDay !== this.props.zeroDay
        ) {
            clearTable = true;
        }
        if (clearTable) {
            this.setState({
                    errorMessage: null,
                    salesSummaryList: [],
                    summaryComplete: false
                },
                () => this.fetchData()
            );
        }
    }

    /* API Calls ------------------------------ */

    /**
     * Get array of aggregated sales by day
     */
    fetchData() {
        // Assemble the URI
        const params = {
            filter: {},
            location_id: this.props.location.id.toString(),
            time_zone_offset: this.props.timeZoneOffset.toString(),
            zero_day: this.props.zeroDay.toString(),
            pagination: {page: 1, perPage: 10000},
            sort: {field: "date", order: "ASC"},
        };
        const resource = reportsResources.SaleSummaryByZeroDay.GET_LIST(params);

        const tokenStatus = checkTokenStatus(null);
        if (tokenStatus.expired) {
            return;
        }

        axios({
            method: 'get',
            url: resource.uri,
            headers: getGetHeaders()
        }).then((result) => {
            if (result.data.payload) {
                this.processData(result.data.payload);
            }
        }).catch((error) => {
            let message = Config.fuse.error.error + this.state.reportName + '.';
            if (error.response) {
                // Server response outside 2xx
                if (error.response.data && error.response.data.error) {
                    message = Config.fuse.error.error + this.state.reportName + ': ' + error.response.data.error;
                }
            } else if (error.request) {
                // No response
                message = Config.fuse.error.no_response + this.state.reportName + '.';
            } else {
                console.log('Error', error.message);
            }
            this.setState({
                errorMessage: message,
                salesSummaryList: [],
                summaryComplete: true,
                tableData: []
            }, () => {
                if (typeof this.props.onSalesComplete === 'function') {
                    this.props.onSalesComplete(true);
                }
            });
        });
    }

    /* Data Processing ------------------------------ */

    /**
     * Process and store sales results
     *
     * @param data
     */
    processData(data) {
        if (data.aggregations && data.aggregations.sales_per_day && data.aggregations.sales_per_day.buckets) {

            const daysObj = data.aggregations.sales_per_day.buckets;
            let daysList = [];
            for (let key in daysObj) {
                if (daysObj.hasOwnProperty(key)) {
                    const day = daysObj[key];
                    let salesTax = (day.cash_credit_card_tax.agg_cash_cc_tax && day.cash_credit_card_tax.agg_cash_cc_tax.value)
                        ? day.cash_credit_card_tax.agg_cash_cc_tax.value : 0;
                    let additionalTax = (day.cash_credit_card_tax.agg_cash_cc_additional_tax && day.cash_credit_card_tax.agg_cash_cc_additional_tax.value)
                        ? day.cash_credit_card_tax.agg_cash_cc_additional_tax.value : 0;
                    let totalTax = salesTax + additionalTax;
                    const item = {};
                    // Create an array item from the date object
                    item.key = key.toString();
                    item.date = key.toString();
                    item.cashGross = (day.gross_cash_sales.agg_cash_sales && day.gross_cash_sales.agg_cash_sales.value)
                        ? toCurrency(day.gross_cash_sales.agg_cash_sales.value, true) : toCurrency(0);
                    item.cashNet = (day.net_cash_sales.agg_cash_net && day.net_cash_sales.agg_cash_net.value)
                        ? toCurrency(day.net_cash_sales.agg_cash_net.value, true) : toCurrency(0);
                    item.ccGross = (day.gross_credit_card_sales.agg_credit_card_sales && day.gross_credit_card_sales.agg_credit_card_sales.value)
                        ? toCurrency(day.gross_credit_card_sales.agg_credit_card_sales.value, true) : toCurrency(0);
                    item.ccNet = (day.net_credit_card_sales.agg_credit_card_net && day.net_credit_card_sales.agg_credit_card_net.value)
                        ? toCurrency(day.net_credit_card_sales.agg_credit_card_net.value, true) : toCurrency(0);
                    item.salesGross = (day.cash_credit_card_currency.agg_cash_cc_currency && day.cash_credit_card_currency.agg_cash_cc_currency.value)
                        ? toCurrency(day.cash_credit_card_currency.agg_cash_cc_currency.value, true) : toCurrency(0);
                    item.salesNet = (day.cash_credit_card_subtotal.agg_cash_cc_subtotal && day.cash_credit_card_subtotal.agg_cash_cc_subtotal.value)
                        ? toCurrency(day.cash_credit_card_subtotal.agg_cash_cc_subtotal.value, true) : toCurrency(0);
                    item.tax = (totalTax) ? toCurrency(totalTax, true) : toCurrency(0);
                    // Exclude dates with no sales
                    const total = parseCurrency(item.cashGross) + parseCurrency(item.cashNet)
                        + parseCurrency(item.ccGross) + parseCurrency(item.ccNet)
                        + parseCurrency(item.salesGross) + parseCurrency(item.salesNet);
                    if (total > 0) {
                        // Add date to the list for table
                        daysList.push(item);
                    }
                }
            }
            if (daysList.length > 0) {
                this.generateSalesSummary(daysList);
            } else {
                this.generateSalesSummary([]);
                this.props.onDisplayMessage('No days with sales found for this Zero Day and Time Zone offset.');
            }

        } else {
            this.props.onDisplayMessage('No days found for this Zero Day and Time Zone offset.');
        }
    }

    /* Table Building ------------------------------ */

    /**
     * Assemble values for Inventory Summary table
     *
     * @param list
     */
    generateSalesSummary(list) {
        let sortedArray = [];
        if (list && list.length > 0) {
            sortedArray = sortArrayByKey(list, 'date', 'DESC');
        }
        // Finalize Sales Summary Table
        this.setState({
            salesSummaryList: sortedArray,
            tableData: sortedArray,
            summaryComplete: true
        });
        this.props.onSalesComplete(true);
    }

    /**
     * Update Footer display based on presence of filter
     *
     * @param event
     */
    handleFilterChange(event) {
        this.setState({
            displayTotals: !(event && event.length > 0)
        });
    }

    /**
     * Update current table data based on sort
     *
     * @param event
     */
    handleSortChange(event) {
        const tableData = getTableState(this.reactTable);
        if (tableData && tableData.length > 0) {
            this.setState({
                tableData: tableData
            });
        }
    }

    /**
     * Handle event to retrieve current state of table data
     *
     * @param event
     */
    handleExportClick = (event) => {
        // CSVLink handles generation and export of CSV
    };

    /* Table Display ------------------------------ */

    render() {

        const {
            location,
            reportName,
            system,
            zeroDay
        } = this.props;

        const {
            displayTotals,
            errorMessage,
            salesSummaryList,
            summaryComplete,
            tableData,
            tableHeaders
        } = this.state;

        const defaultSort = setDefaultTableSort('location_id');

        const totalsStyle = (displayTotals) ? {display: 'block'} : {display: 'none'};

        // ReactTable headers, columns and footer totals for Sales Summary

        const salesColumns = [
            {
                Header: '',
                columns: [
                    {
                        Header: 'Date',
                        accessor: 'date',
                        Footer: (<span style={{...tableStyles.footer, ...totalsStyle}}>&nbsp;</span>)
                    }
                ]
            },
            {
                Header: 'Cash Sales',
                columns: [
                    {
                        Header: 'Gross Cash Sales',
                        accessor: 'cashGross',
                        sortMethod: (a, b) => sortCurrency(a, b),
                        filterable: false,
                        style: tableStyles.right,
                        Footer: (
                            <span style={{...tableStyles.footer, ...totalsStyle}}>
                                {toCurrency(addFromColumn(salesSummaryList, 'cashGross'))}
                            </span>
                        )
                    },
                    {
                        Header: 'Net Cash Sales',
                        accessor: 'cashNet',
                        sortMethod: (a, b) => sortCurrency(a, b),
                        filterable: false,
                        style: tableStyles.right,
                        Footer: (
                            <span style={{...tableStyles.footer, ...totalsStyle}}>
                                {toCurrency(addFromColumn(salesSummaryList, 'cashNet'))}
                            </span>
                        )
                    }
                ]
            },
            {
                Header: 'Credit Card Sales',
                columns: [
                    {
                        Header: 'Gross Credit Card Sales',
                        accessor: 'ccGross',
                        sortMethod: (a, b) => sortCurrency(a, b),
                        filterable: false,
                        style: tableStyles.right,
                        Footer: (
                            <span style={{...tableStyles.footer, ...totalsStyle}}>
                                {toCurrency(addFromColumn(salesSummaryList, 'ccGross'))}
                            </span>
                        )
                    },
                    {
                        Header: 'Net Credit Card Sales',
                        accessor: 'ccNet',
                        sortMethod: (a, b) => sortCurrency(a, b),
                        filterable: false,
                        style: tableStyles.right,
                        Footer: (
                            <span style={{...tableStyles.footer, ...totalsStyle}}>
                                {toCurrency(addFromColumn(salesSummaryList, 'ccNet'))}
                            </span>
                        )
                    }
                ]
            },
            {
                Header: 'All Sales',
                columns: [
                    {
                        Header: 'Gross Sales',
                        accessor: 'salesGross',
                        sortMethod: (a, b) => sortCurrency(a, b),
                        filterable: false,
                        style: tableStyles.right,
                        Footer: (
                            <span style={{...tableStyles.footer, ...totalsStyle}}>
                                {toCurrency(addFromColumn(salesSummaryList, 'salesGross'))}
                            </span>
                        )
                    },
                    {
                        Header: 'Total Tax',
                        accessor: 'tax',
                        sortMethod: (a, b) => sortCurrency(a, b),
                        filterable: false,
                        style: tableStyles.right,
                        Footer: (
                            <span style={{...tableStyles.footer, ...totalsStyle}}>
                                {toCurrency(addFromColumn(salesSummaryList, 'tax'))}
                            </span>
                        )
                    },
                    {
                        Header: 'Net Sales',
                        accessor: 'salesNet',
                        sortMethod: (a, b) => sortCurrency(a, b),
                        filterable: false,
                        style: tableStyles.right,
                        Footer: (
                            <span style={{...tableStyles.footer, ...totalsStyle}}>
                                {toCurrency(addFromColumn(salesSummaryList, 'salesNet'))}
                            </span>
                        )
                    }
                ]
            }
        ];

        return (
            <div>
                {!summaryComplete &&
                    <div className="loader-container">
                        <h4>Generating Sales Summary...</h4>
                        <CircularProgress
                            size={24}
                            thickness={2}
                            className="loader"
                        />
                    </div>
                }
                {summaryComplete &&
                    <div className="reports-table-container">

                        {/* Table Header: Sales Summary */}
                        <div className="reports-table-title table-table-header">
                            <div className="btn-report-container report-header-button-container">
                                {/* Export Sales Summary */}
                                <FlatButton
                                    primary
                                    type="button"
                                    label=""
                                    style={commonStyles.headerButton}
                                >
                                    <ExportIcon style={commonStyles.headerButtonIcon}/>
                                    <CSVLink
                                        data={tableData}
                                        headers={tableHeaders}
                                        filename={reportName + '-Sales-Summary-' + system + '-' + location.id + '-' + zeroDay + '.csv'}
                                        onClick={(e) => this.handleExportClick(e)}
                                        target="_blank"
                                        style={commonStyles.headerButtonLink}
                                    >
                                        Export Sales Summary
                                    </CSVLink>
                                </FlatButton>
                            </div>
                            <h3 className="table-table-header-title">
                                <SalesSummaryIcon style={commonStyles.summaryIcon}/>
                                <span>Sales Summary</span>
                            </h3>
                        </div>

                        {/* Table: Sales Summary */}
                        <ExpandableTableContainer>
                            <ReactTable
                                key="table-sales-summary"
                                data={salesSummaryList}
                                columns={salesColumns}
                                defaultPageSize={5000}
                                defaultSorted={defaultSort}
                                filterable
                                defaultFilterMethod={filterCaseInsensitive}
                                onFilteredChange={this.handleFilterChange}
                                onSortedChange={this.handleSortChange}
                                ref={(r) => this.reactTable = r}
                                showPagination={false}
                                resizable={false}
                                minRows={1}
                                className="-striped summary-table"
                            />
                        </ExpandableTableContainer>

                        {/* Error and Messaging */}
                        {errorMessage &&
                            <div className="error">
                                <p>{errorMessage}</p>
                            </div>
                        }
                    </div>
                }
            </div>
        )
    }
}

SettlementReportSales.propTypes = {
    addField: PropTypes.bool,
    location: PropTypes.object.isRequired,
    onDisplayMessage: PropTypes.func,
    onSalesComplete: PropTypes.func,
    reportName: PropTypes.string,
    reportType: PropTypes.string,
    system: PropTypes.string,
    timeZoneOffset: PropTypes.string.isRequired,
    zeroDay: PropTypes.string.isRequired
};

SettlementReportSales.defaultProps = {
    addField: true,
    location: {
        dbaPriceLevel: null,
        id: null,
        name: null,
        priceLevel: null,
        productType: null
    },
    reportName: 'Settlement-Report',
    reportType: 'settlement',
    timeZoneOffset: null,
    zeroDay: null
};

const enhance = compose(
    connect()
);

export default enhance(SettlementReportSales);



// WEBPACK FOOTER //
// ./src/Fuse/components/SettlementReportSales.js